import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Mail,
  Phone,
  MapPin,
  Clock,
  Loader2,
} from "lucide-react";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  inquiry_type: "",
  message: "",
};

function Eyebrow({ children, tone = "zinc" }) {
  return (
    <div
      className={`flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] ${
        tone === "light" ? "text-zinc-400" : "text-zinc-500"
      }`}
    >
      <span className="h-[6px] w-[6px] bg-orange-500" />
      {children}
    </div>
  );
}

const fieldClass =
  "w-full border border-zinc-300 bg-white px-4 py-3 font-body text-sm text-zinc-950 placeholder:text-zinc-400 outline-none transition-colors focus:border-zinc-950 focus:ring-2 focus:ring-zinc-950";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      err.full_name = "Please enter your full name.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      err.email = "Please enter a valid email address.";
    if (!form.phone.trim() || form.phone.trim().length < 6)
      err.phone = "Please enter a valid phone number.";
    if (!form.inquiry_type) err.inquiry_type = "Please select an inquiry type.";
    if (!form.message.trim() || form.message.trim().length < 5)
      err.message = "Please add a short message (min 5 characters).";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        company_name: form.company_name.trim() ? form.company_name.trim() : null,
      };
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        "Could not send message. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 pt-20 pb-10 md:px-12 md:pt-28 md:pb-16 lg:px-16">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tighter text-zinc-950 md:text-7xl lg:text-8xl">
            Let's <span className="italic font-light">connect.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600">
            Whether you're exploring a product inquiry, distribution
            opportunity, or CSR partnership — we're just a message away.
          </p>
        </div>
      </section>

      {/* SPLIT: DETAILS + FORM */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-0 md:grid-cols-12">
          {/* LEFT: typography-heavy details */}
          <aside className="border-zinc-200 bg-zinc-950 p-8 text-white md:col-span-5 md:border-r md:p-14">
            <Eyebrow tone="light">Registered Office</Eyebrow>
            <div className="mt-6 font-heading text-2xl font-bold leading-tight text-white md:text-3xl">
              Agrim Ventures Private Limited
            </div>
            <p className="mt-4 text-zinc-400">
              A2-Ashok Nagar, Udaipur
              <br />
              Rajasthan (India) - Pin 313001
            </p>
            <div className="mt-4 font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              CIN: U74999RJ2022PTC080381
            </div>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="mt-1 h-5 w-5 text-orange-400" strokeWidth={1.5} />
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    Email
                  </div>
                  <a
                    href="mailto:info@agrimventures.co.in"
                    data-testid="contact-email-link"
                    className="mt-1 block text-white hover:underline"
                  >
                    info@agrimventures.co.in
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="mt-1 h-5 w-5 text-orange-400" strokeWidth={1.5} />
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    Phone
                  </div>
                  <a
                    href="tel:+912942941033"
                    data-testid="contact-phone-link"
                    className="mt-1 block text-white hover:underline"
                  >
                    0294-2941033
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="mt-1 h-5 w-5 text-orange-400" strokeWidth={1.5} />
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    Business Hours
                  </div>
                  <p className="mt-1 text-white">
                    Mon – Sat · 10:00 AM – 6:30 PM
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="mt-1 h-5 w-5 text-orange-400" strokeWidth={1.5} />
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    Distribution
                  </div>
                  <p className="mt-1 text-white">
                    Wide network across India
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* RIGHT: form */}
          <div className="bg-white p-8 md:col-span-7 md:p-14">
            <Eyebrow>Get In Touch</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight text-zinc-950 md:text-4xl">
              Send us a message.
            </h2>

            <form
              onSubmit={submit}
              className="mt-10 space-y-6"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label
                    htmlFor="full_name"
                    className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                  >
                    Full Name *
                  </Label>
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Jane Mehta"
                    data-testid="input-full-name"
                    className={`mt-2 ${fieldClass}`}
                  />
                  {errors.full_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.full_name}</p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="company_name"
                    className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                  >
                    Company Name
                  </Label>
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    value={form.company_name}
                    onChange={update("company_name")}
                    placeholder="Acme Polymers Pvt Ltd"
                    data-testid="input-company"
                    className={`mt-2 ${fieldClass}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label
                    htmlFor="email"
                    className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                  >
                    Email Address *
                  </Label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                    data-testid="input-email"
                    className={`mt-2 ${fieldClass}`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="phone"
                    className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                  >
                    Phone Number *
                  </Label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98xxxxxxxx"
                    data-testid="input-phone"
                    className={`mt-2 ${fieldClass}`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="inquiry_type"
                  className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                >
                  Inquiry Type *
                </Label>
                <Select
                  value={form.inquiry_type}
                  onValueChange={update("inquiry_type")}
                >
                  <SelectTrigger
                    id="inquiry_type"
                    data-testid="select-inquiry-type"
                    className="mt-2 h-[46px] rounded-none border-zinc-300 bg-white text-sm text-zinc-950 focus:border-zinc-950 focus:ring-2 focus:ring-zinc-950"
                  >
                    <SelectValue placeholder="Select an inquiry type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-300">
                    <SelectItem value="Product Inquiry">
                      Product Inquiry
                    </SelectItem>
                    <SelectItem value="Distributor Inquiry">
                      Distributor Inquiry
                    </SelectItem>
                    <SelectItem value="CSR Partnership">
                      CSR Partnership
                    </SelectItem>
                    <SelectItem value="General">General</SelectItem>
                  </SelectContent>
                </Select>
                {errors.inquiry_type && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.inquiry_type}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="message"
                  className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-600"
                >
                  Your Message *
                </Label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us briefly what you're looking for..."
                  data-testid="input-message"
                  className={`mt-2 resize-none ${fieldClass}`}
                />
                {errors.message && (
                  <p className="mt-1 text-xs text-red-600">{errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-inquiry"
                className="group inline-flex items-center justify-between gap-3 bg-zinc-950 px-7 py-4 font-mono-plex text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* SINGLE POINT OF CONTACT */}
      <section
        data-testid="contact-single-poc"
        className="border-b border-zinc-200 bg-zinc-50"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-center">
            <div className="md:col-span-7">
              <Eyebrow>One Point of Contact</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                Whatever your inquiry — reach us here.
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-600">
                Product, distribution, or CSR partnership — write to us at a
                single address and the right person will respond.
              </p>
            </div>
            <div className="md:col-span-5">
              <a
                href="mailto:info@agrimventures.co.in"
                data-testid="single-poc-email"
                className="group flex flex-col justify-between border border-zinc-950 bg-white p-8 transition-colors hover:bg-zinc-950 hover:text-white md:p-10"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center border border-zinc-950 text-zinc-950 transition-colors group-hover:border-white group-hover:text-white">
                    <Mail className="h-5 w-5" strokeWidth={1.4} />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                </div>
                <div className="mt-12">
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500 group-hover:text-zinc-300">
                    Write to us
                  </div>
                  <div className="mt-2 font-heading text-2xl font-bold tracking-tight md:text-3xl">
                    info@agrimventures.co.in
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="contact-map">
        <div className="mx-auto max-w-[1400px] px-6 pt-20 md:px-12 md:pt-24 lg:px-16">
          <Eyebrow>Find Us</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-heading text-3xl font-bold tracking-tight text-zinc-950 md:text-4xl">
            Visit our registered office.
          </h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1400px] px-6 pb-24 md:px-12 lg:px-16">
          <div className="relative h-[360px] w-full overflow-hidden border border-zinc-200 md:h-[460px]">
            <iframe
              title="Agrim Ventures Office Location"
              src="https://www.google.com/maps?q=Ashok+Nagar,Udaipur,Rajasthan+313001&output=embed"
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{
                border: 0,
                filter: "grayscale(100%) contrast(1.05)",
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
